import '@/styles/global.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from '@/auth/AuthProvider';
import { UserProvider } from '@/contexts/UserContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AppRouter } from '@/router/AppRouter';
import { TeamsConfigPage } from '@/teams/TeamsConfigPage';
import { ForensicsPage } from '@/views/ForensicsPage';
import '@/i18n';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { sinBase } from '@/utils/basePath';
// Restaura el import perdido al borrar App.tsx (2b9da5e, unificación de BrowserRouter): esa hoja
// tenía `import '@/styles/global.css'` — sin equivalente en ningún archivo nuevo, quedó huérfana.
// Sin ella no cargan, entre otras, las reglas de breakpoint móvil `.side-nav { display: none }` /
// `.mobile-bottom-nav { display: flex }` (`≤768px`): el riel de escritorio completo (incluido el
// botón "Administración" y su flyout) seguía visible en móvil al mismo tiempo que el nav inferior,
// encontrado al verificar con Playwright el fix de huecos de Task 12.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,   // 5 min — avoids re-fetching on every mount
      retry: 1,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');

// 🔴 Estas dos comprobaciones corren ANTES de montar el router, sobre el pathname CRUDO, así que
// el `basename` no las cubre: hay que quitarles el prefijo del ambiente a mano. Sin esto, en el
// ambiente de desarrollo el pathname es '/dev/config' y el regex anclado a '^/' no casa: la página
// de configuración de la pestaña de Teams y la herramienta forense simplemente no existirían allí.
const rutaRelativa = sinBase(window.location.pathname);
const isConfigRoute = /^\/config\/?$/.test(rutaRelativa);
// Ruta secreta: herramienta forense personal. No está enlazada en ningún menú. El candado real
// (por UPN) vive en el backend; esto solo decide qué se renderiza.
const isForensicsRoute = /^\/_forensics\/?$/.test(rutaRelativa);

ReactDOM.createRoot(container).render(
  <React.StrictMode>
    {isConfigRoute ? (
      <TeamsConfigPage />
    ) : isForensicsRoute ? (
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ForensicsPage />
        </AuthProvider>
      </QueryClientProvider>
    ) : (
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <UserProvider>
            <ThemeProvider>
              <AppRouter />
            </ThemeProvider>
          </UserProvider>
        </AuthProvider>
        {/* buttonPosition="bottom-left": solo-DEV, nunca llega a producción (`import.meta.env.DEV`),
            pero en la esquina inferior derecha por defecto se apilaba con el FAB de usuario
            (`.ucfab-btn`) y el nav inferior móvil (`.mobile-bottom-nav`) — estorbaba las pruebas
            manuales en viewport <768px (encontrado 2026-09-04). */}
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
      </QueryClientProvider>
    )}
  </React.StrictMode>
);
